import {
  Box,
  Breadcrumbs,
  Container,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import { RouteLink } from "@/components/common/styles";
import {
  FRAME,
  MID,
  NOT_WIDE,
  SITE_STEPS,
  TINY,
  WIDE,
} from "@/components/marketing/tokens";
import { navClearance } from "@/components/marketing/header/tokens";
import {
  BLOG_BASE_PATH,
  blogPostCanonicalUrl,
  type BlogPost,
  blogCategoryPath,
  type CallToAction,
} from "@/content/types";
import ArticleBody from "@/components/marketing/article/ArticleBody";
import ArticleContent from "@/components/marketing/article/ArticleContent";
import ArticleContents from "@/components/marketing/article/ArticleContents";
import MobileContents from "@/components/marketing/article/MobileContents";
import AuthorCard from "@/components/marketing/article/AuthorCard";
import MarketingHead from "@/components/marketing/article/MarketingHead";
import CategoryLabel from "@/components/marketing/article/CategoryLabel";
import PostCtaCard from "./PostCtaCard";
import PostCtaPopup from "./PostCtaPopup";
import PostFaqs from "./PostFaqs";
import ShareRow from "@/components/marketing/article/ShareRow";
import updatedOn from "@/components/marketing/article/updatedOn";

/**
 * Every width here was measured off the published article, where the body is
 * fluid between the rail and the edge and only `BODY_WIDTH` caps it.
 */
const CONTENTS_WIDTH = 400;
const BODY_WIDTH = 832;
/** The body's own leading space; the row itself has no gap. */
const BODY_INSET = "32px";

/** Measured at 1280, 991, 767 and 375: the article's clearance steps at 768. */
const CLEARANCE = { wide: 112, lg: 112, md: 96, sm: 88 };

/** Where the published rail parks itself once the page scrolls under it. */
const RAIL_TOP = 100;

/**
 * The hero is a share of the row rather than a width, and it crops: the wrapper
 * holds the ratio and the image covers it, so a portrait or a square asset
 * still draws the same band. The published ratio is 56%, not 16:9.
 *
 * It gives up the inset once the rail is gone and spans the row instead.
 */
const HERO_WIDTH = "80%";
const HERO_RATIO = "25 / 14";
const HERO_RADIUS = 16;
/** What the published hero declares; see the note on the card's. */
const HERO_SIZES = "100vw";

/**
 * The glyph the published breadcrumb draws inline ahead of "Blog". It is drawn
 * rather than vendored for the same reason the header's are: it strokes with
 * `currentColor`, so it has to inherit the link's colour through its states.
 */
function BlogCrumbIcon() {
  return (
    <SvgIcon viewBox="0 0 14 14" sx={{ fontSize: 14 }}>
      <path
        d="M2.33301 2.46316V11.5375C2.33301 12.2532 2.92976 12.8337 3.66651 12.8337H10.3328C11.0696 12.8337 11.6663 12.2532 11.6663 11.5375V4.92599C11.6663 4.61657 11.5434 4.31983 11.3246 4.10103C11.1058 3.88224 10.8091 3.75933 10.4997 3.75933H3.66651C2.92976 3.75933 2.33301 3.17949 2.33301 2.46316ZM2.33301 2.46316C2.33301 1.74741 2.92976 1.16699 3.66651 1.16699H8.99992C9.73609 1.16699 10.3328 1.74741 10.3328 2.46316V3.75933"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgIcon>
  );
}

function PostHeader({ post }: { post: BlogPost }) {
  const updated = updatedOn(post.updatedAt);

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={[
          (theme) => ({
            color: alpha(theme.palette.text.secondary, 0.6),
            my: 3,
            // The row never wraps at any width: a title too long for it wraps
            // its own words instead, the crumbs giving way. Only on a phone
            // does the row scroll, and only there does it stop centring,
            // bisected: 480 keeps the centred row, 479 left-aligns it and keeps
            // every crumb whole, so a long title runs out of the side.
            // Scrolling at desktop draws a track the published page has not got.
            "& .MuiBreadcrumbs-ol": {
              justifyContent: "center",
              flexWrap: "nowrap",
            },
            "& .MuiBreadcrumbs-li": { flex: "0 1 auto" },
            [TINY]: {
              overflowX: "scroll",
              "& .MuiBreadcrumbs-ol": { justifyContent: "flex-start" },
              "& .MuiBreadcrumbs-li": { flex: "0 0 auto" },
            },
            // The slash is set in the crumbs' own type, or it leads the row
            // taller than they do.
            "& .MuiBreadcrumbs-separator": {
              mx: "6.08px",
              fontSize: 15,
              lineHeight: "22.5px",
            },
            "& .MuiTypography-root": { fontSize: 15, lineHeight: "22.5px" },
          }),
          // A later element, so it is written after the rules it overrides: in
          // one object MUI hoists this step, a theme breakpoint, above them.
          {
            [WIDE]: {
              my: 3.5,
              "& .MuiBreadcrumbs-separator": {
                fontSize: 16,
                lineHeight: "24px",
              },
              "& .MuiTypography-root": { fontSize: 16, lineHeight: "24px" },
            },
          },
        ]}
      >
        <RouteLink
          to={BLOG_BASE_PATH}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "text.primary",
            "&:hover": { color: "primary.dark" },
          }}
        >
          <BlogCrumbIcon />
          <Typography color="inherit">Blog</Typography>
        </RouteLink>
        <Typography color="inherit">{post.title}</Typography>
      </Breadcrumbs>

      {/* `.b-header-top_content`: centred, a little more open below the wide
          step, and left-aligned below the phone step. */}
      <Stack
        component="header"
        gap={2}
        alignItems="center"
        sx={{
          pt: 3,
          [NOT_WIDE]: { rowGap: "9.6px" },
          [TINY]: { alignItems: "flex-start" },
        }}
      >
        <Stack direction="row" gap={1} alignItems="center">
          {updated ? (
            <Typography
              variant="body2"
              sx={{
                display: "flex",
                // It keeps its width beside the category, which gives way
                // instead, and wraps its two parts before it shrinks them.
                flexShrink: 0,
                flexWrap: "wrap",
                columnGap: "6.08px",
                lineHeight: "21px",
                mr: "12px",
                // A size down on a phone, and closer to the chip beside it:
                // at 14px the pair no longer fits 375 and both lines wrap.
                [TINY]: { fontSize: 13, lineHeight: "19.5px", mr: "6.4px" },
              }}
            >
              {/* Two boxes, not one string: the published pair are separated by
                  a 6.08px gap where a space would be 3.44. The space between
                  them survives in the text, where it is not rendered. */}
              <Box component="span">Updated on</Box>{" "}
              <Box component="span">{updated}</Box>
            </Typography>
          ) : null}
          {post.category ? (
            <CategoryLabel
              category={post.category}
              variant="post"
              linked
              href={blogCategoryPath(post.category.slug)}
            />
          ) : null}
        </Stack>

        <Typography
          variant="h1"
          align="center"
          sx={{ [TINY]: { textAlign: "left" } }}
        >
          {post.title}
        </Typography>
      </Stack>

      {post.image ? (
        <Box
          sx={(theme) => ({
            width: "100%",
            mx: "auto",
            mt: 5,
            aspectRatio: HERO_RATIO,
            borderRadius: `${HERO_RADIUS}px`,
            overflow: "hidden",
            [theme.breakpoints.up(SITE_STEPS.wide)]: {
              width: HERO_WIDTH,
              mt: 5.5,
            },
          })}
        >
          <Box
            component="img"
            src={post.image.url}
            alt={post.image.alt || post.title}
            sizes={HERO_SIZES}
            srcSet={post.image.srcset}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      ) : null}
    </>
  );
}

/**
 * The published template prints its call to action on every post, so a post
 * without one gets the card with nothing in it, in all three places: the frame,
 * a button with no label and the animation.
 */
const NO_CTA: CallToAction = {
  title: "",
  subtitle: "",
  buttonLabel: "",
  href: "",
};

export default function BlogPostPage({ post }: { post: BlogPost }) {
  const cta = post.cta ?? NO_CTA;
  return (
    <>
      <MarketingHead
        title={post.seoTitle}
        description={post.seoDescription}
        canonicalOverride={post.seoCanonical}
        socialTitle={post.seoSocialTitle}
        canonical={blogPostCanonicalUrl(post.slug)}
        // 19 posts carry only a thumbnail, and the published page emits an
        // empty `og:image` for them, so those have no social preview today.
        // Falling back is a deliberate improvement rather than a reproduction.
        shareImage={(post.image ?? post.thumbnail)?.url}
      />

      <Box component="main">
        <Container
          maxWidth={false}
          disableGutters
          sx={(theme) => ({
            // The cap is border-box, so the gutter is added to it rather than
            // spent out of the content column, and it is kept at every width.
            maxWidth: `calc(${FRAME.blog}px + ${theme.spacing(3)} * 2)`,
            px: 3,
            pb: 0,
            ...navClearance(CLEARANCE),
          })}
        >
          <PostHeader post={post} />

          {/* Above the row, not inside the body: the published page runs the
            walkthrough the full width of the frame and starts the contents
            rail underneath it. */}
          {post.video ? <PostVideo video={post.video} /> : null}

          <Box
            sx={{
              mt: 5.5,
              display: "flex",
              alignItems: "flex-start",
              // Not a gap and not centred: the published row spreads its slack in
              // three equal parts, outside the rail, between the two, and after
              // the body, and the body carries the 32 of its own. Once the rail
              // is gone the body is the row's one item and starts at its edge
              // (`space-between`), where spreading would centre it.
              justifyContent: "space-evenly",
              [NOT_WIDE]: { justifyContent: "space-between" },
            }}
          >
            <Stack
              gap={4}
              sx={(theme) => ({
                width: CONTENTS_WIDTH,
                flexShrink: 0,
                position: "sticky",
                top: RAIL_TOP,
                display: "none",
                [theme.breakpoints.up(SITE_STEPS.wide)]: { display: "flex" },
              })}
            >
              <ArticleContents headings={post.headings} />
              <PostCtaCard cta={cta} />
            </Stack>

            <Stack
              gap={3}
              sx={(theme) => ({
                width: "100%",
                maxWidth: BODY_WIDTH,
                mb: "80px",
                // The published column spends less below itself once the rail is
                // gone, and less again below the mobile step (80, 64, 48); the
                // FAQ band that follows tightens by the same step.
                [theme.breakpoints.down(SITE_STEPS.wide)]: { mb: 6 },
                [MID]: { mb: "48px" },
                // Only once the rail is beside it: below that the body is the
                // row's one child and this would just push it off centre.
                [theme.breakpoints.up(SITE_STEPS.wide)]: { ml: BODY_INSET },
              })}
            >
              <ArticleBody className="blog-content is-blog">
                <ArticleContent body={post.body} />
              </ArticleBody>
              <AuthorCard author={post.author} />
              <ShareRow url={blogPostCanonicalUrl(post.slug)} />
              {/* The rail's card again, below the article: the published page
                carries two copies of it and shows whichever the width allows. */}
              <Box
                sx={(theme) => ({
                  // Alone among the column's children it carries a lead of its
                  // own on top of the gap, as the published card does, until
                  // the mobile step, where it drops it.
                  mt: 3,
                  [theme.breakpoints.up(SITE_STEPS.wide)]: {
                    display: "none",
                  },
                  [MID]: { mt: 0 },
                })}
              >
                <PostCtaCard cta={cta} variant="below" />
              </Box>
            </Stack>
          </Box>
        </Container>

        <PostFaqs faqs={post.faqs} />
      </Box>

      <MobileContents headings={post.headings} />
      <PostCtaPopup cta={cta} />
    </>
  );
}

/**
 * The walkthrough nineteen how-to posts open with.
 *
 * Above the body rather than inside it, because that is where the published
 * page draws it, Webflow keeps it in a field of its own. Built as the published
 * band is: four fifths of the frame (the whole of it below the wide step), a
 * rounded box 56% as tall as it is wide, and the player hung inside it at the
 * embed vendor's own 854 by 480, a shade taller, so the box crops its last
 * pixels.
 */
function PostVideo({ video }: { video: NonNullable<BlogPost["video"]> }) {
  return (
    <Box
      sx={{
        width: "80%",
        mx: "auto",
        // 48 under the title and 32 below the wide step, as the published page
        // sets it; the header above closes tight and left the player against
        // the heading.
        mt: "48px",
        mb: 4,
        [NOT_WIDE]: { width: 1, mt: "32px" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 1,
          pt: "56%",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        <Box
          component="iframe"
          src={video.url}
          title={video.title || "Video"}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 1,
            height: "auto",
            aspectRatio: "854 / 480",
            border: 0,
          }}
        />
      </Box>
    </Box>
  );
}
